/**
 * disclosureModel — pure helpers shared by the Disclosures screens.
 *
 * The LIVE-version rule mirrors the server exactly
 * (apps/PCBRetailApi/Services/DisclosureVersionSelection.cs, 2026-10-07):
 *   live = status 'Active' AND effectiveTo empty; newest effectiveFrom, then the
 *   highest versionId, wins. No Active version = the template is not in any live
 *   package (it is never replaced by a Draft).
 *
 * The applicability sentences mirror DisclosureRenderOrchestrator.ResolveAsync
 * (Phase 1 product filters, Phase 2 customer-type rules, Phase 3 universal floor,
 * Filter 4 presence conditions). Labels come from GET /v3/admin/disclosures/options.
 */

import type {
  DisclosureTemplateRow,
  DisclosureVersionRow,
} from '@/services/disclosuresApi';
import type { DisclosureOptions, VocabularyItem } from '@/services/disclosuresAdminApi';

export const VERSION_STATUS_ORDER = ['Draft', 'InReview', 'Approved', 'Active', 'Retired'] as const;

/** Plain-language status names (InReview is shown as "Pending approval"). */
export function statusLabel(status: string | null | undefined): string {
  switch (String(status ?? '')) {
    case 'Draft':
      return 'Draft';
    case 'InReview':
      return 'Pending approval';
    case 'Approved':
      return 'Approved, not yet active';
    case 'Active':
      return 'Active';
    case 'Retired':
      return 'Retired';
    default:
      return status ? String(status) : '—';
  }
}

export function versionsFor(
  versions: ReadonlyArray<DisclosureVersionRow>,
  code: string,
): DisclosureVersionRow[] {
  return versions
    .filter((v) => String(v.templateCode ?? '').toUpperCase() === code.toUpperCase())
    .slice()
    .sort((a, b) => {
      const at = Date.parse(String(a.effectiveFrom ?? '')) || 0;
      const bt = Date.parse(String(b.effectiveFrom ?? '')) || 0;
      if (bt !== at) return bt - at;
      return Number(b.versionId ?? 0) - Number(a.versionId ?? 0);
    });
}

export function isLive(v: DisclosureVersionRow): boolean {
  return String(v.status ?? '') === 'Active' && (v.effectiveTo === null || v.effectiveTo === undefined || v.effectiveTo === '');
}

/** The version live packages use, or null. `versions` already sorted newest-first. */
export function liveVersion(sorted: ReadonlyArray<DisclosureVersionRow>): DisclosureVersionRow | null {
  return sorted.find(isLive) ?? null;
}

export function pendingVersions(sorted: ReadonlyArray<DisclosureVersionRow>): DisclosureVersionRow[] {
  return sorted.filter((v) => ['Draft', 'InReview', 'Approved'].includes(String(v.status ?? '')));
}

export function splitCsv(v: unknown): string[] {
  if (v === null || v === undefined) return [];
  return String(v)
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

export function joinCsv(values: ReadonlyArray<string>): string | null {
  const clean = Array.from(new Set(values.map((x) => x.trim()).filter(Boolean)));
  return clean.length === 0 ? null : clean.join(',');
}

export function splitCondition(v: unknown): string[] {
  if (v === null || v === undefined) return [];
  return String(v)
    .split('|')
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

export function itemFor(
  items: ReadonlyArray<VocabularyItem> | undefined,
  value: unknown,
): VocabularyItem | null {
  const v = String(value ?? '');
  return items?.find((i) => i.value.toLowerCase() === v.toLowerCase()) ?? null;
}

export function requiresSignature(t: DisclosureTemplateRow): boolean {
  const r = String(t.signatureRequirement ?? '').trim();
  return r.length > 0 && r !== 'none';
}

/** Plain-language "who gets this document" lines. */
export function describeApplicability(
  t: DisclosureTemplateRow,
  live: DisclosureVersionRow | null,
  options: DisclosureOptions | null,
): string[] {
  const lines: string[] = [];
  const code = String(t.code ?? '');
  const layer = String(t.layer ?? '');
  const layerItem = itemFor(options?.layers, layer);

  const ruleRowsFor = (options?.entityRequiredDocs ?? []).filter(
    (d) => d.docCode.toUpperCase() === code.toUpperCase(),
  );
  if (t.isActive === false) {
    lines.push('Switched off: the package builder does not add it from product filters.');
    if (ruleRowsFor.length > 0) {
      lines.push(
        `Still added by the customer-type rules for: ${ruleRowsFor.map((d) => d.customerType).join(', ')} ` +
          '(those rules do not check this switch).',
      );
    }
    return lines;
  }
  if (String(t.audience ?? 'fsr-officer') !== 'fsr-officer') {
    const aud = itemFor(options?.audiences, t.audience);
    lines.push(`Audience is "${aud?.label ?? String(t.audience)}", so account-opening packages do not include it.`);
  }
  if (!live) {
    lines.push('No active version, so it is not in any live package. Activate a version to use it.');
  }

  const floor = options?.universalFloorCodes ?? [];
  if (floor.some((c) => c.toUpperCase() === code.toUpperCase())) {
    lines.push('Always in every account-opening package, whatever the filters below say.');
  }

  if (layer === 'universal' || layer === 'product' || layer === 'ownership') {
    const cats = splitCsv(t.productCategory).filter((c) => c.toUpperCase() !== 'ALL');
    const prods = splitCsv(t.applicableProducts);
    const ents = splitCsv(t.applicableEntityTypes).filter((c) => c.toUpperCase() !== 'ALL');
    const prodName = (c: string): string => {
      const p = options?.products.find((x) => x.code.toUpperCase() === c.toUpperCase());
      return p ? `${p.name} (${p.code})` : c;
    };
    const entName = (c: string): string => {
      const e = options?.entityTypes.find((x) => x.value.toUpperCase() === c.toUpperCase());
      return e ? e.label : c;
    };
    lines.push(
      cats.length === 0
        ? 'Product types: every product type.'
        : `Product types: only when the application has a ${cats.join(' or ')} product.`,
    );
    if (prods.length > 0) lines.push(`Products: only when one of these is opened — ${prods.map(prodName).join(', ')}.`);
    lines.push(
      ents.length === 0
        ? 'Customer types: every customer type.'
        : `Customer types: only for ${ents.map(entName).join(', ')}.`,
    );
  } else if (layerItem) {
    lines.push(`${layerItem.label}: ${layerItem.meaning}`);
  } else if (layer) {
    lines.push(`Layer "${layer}" is not one the package builder adds automatically.`);
  }

  const ruleRows = ruleRowsFor;
  if (ruleRows.length > 0) {
    const names = ruleRows.map((d) => {
      const e = options?.entityTypes.find((x) => x.value.toUpperCase() === d.customerType.toUpperCase());
      return `${e?.label ?? d.customerType}${d.isMandatory ? '' : ' (optional)'}`;
    });
    lines.push(`Customer-type rules also require it for: ${names.join(', ')}.`);
  }

  const cond = splitCondition(live?.conditionExpression);
  if (cond.length > 0) {
    const labels = cond.map((c) => itemFor(options?.presenceConditions, c)?.label ?? `unknown condition "${c}"`);
    lines.push(`Only when: ${labels.join(' OR ')} (from the active version).`);
  }
  return lines;
}

export function formatWhen(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

// ── Variant selector — mirror of VariantResolverService.ParseSelector ─────────
// Grammar the server reads: one or more `planCode in ('X','Y')` clauses (ANDed) and
// an optional `aliasTARGET_CODE` (letters, digits, _ immediately after "alias").
// Anything else is ignored by the server (no aliasing).

export interface ParsedSelector {
  planCodeSets: string[][];
  aliasTarget: string | null;
  /** Text present that the server will not act on (e.g. "alias→X"). */
  ignored: string[];
}

export function parseSelector(expr: unknown): ParsedSelector {
  const text = String(expr ?? '');
  const planCodeSets: string[][] = [];
  const planRe = /planCode\s+in\s*\(\s*([^)]+)\s*\)/gi;
  let m: RegExpExecArray | null;
  let rest = text;
  while ((m = planRe.exec(text)) !== null) {
    const raw = m[1] ?? '';
    planCodeSets.push(
      raw
        .split(',')
        .map((x) => x.trim().replace(/^['"]|['"]$/g, ''))
        .filter((x) => x.length > 0),
    );
    rest = rest.replace(m[0], ' ');
  }
  const aliasM = /\balias([A-Za-z0-9_]+)/.exec(text);
  const aliasTarget = aliasM ? aliasM[1] ?? null : null;
  if (aliasM) rest = rest.replace(aliasM[0], ' ');
  const ignored = rest.trim().length > 0 ? [rest.trim()] : [];
  return { planCodeSets, aliasTarget, ignored };
}

export function composeSelector(planCodes: ReadonlyArray<string>, aliasTarget: string): string | null {
  const parts: string[] = [];
  const codes = planCodes.map((c) => c.trim()).filter(Boolean);
  if (codes.length > 0) parts.push(`planCode in (${codes.map((c) => `'${c}'`).join(',')})`);
  if (aliasTarget.trim()) parts.push(`alias${aliasTarget.trim()}`);
  return parts.length === 0 ? null : parts.join(' ');
}

export function describeSelector(expr: unknown): string {
  if (expr === null || expr === undefined || String(expr).trim() === '') return 'Always matches; no text swap.';
  const p = parseSelector(expr);
  const when =
    p.planCodeSets.length === 0
      ? 'Any plan'
      : p.planCodeSets.map((set) => `plan ${set.join(' or ')}`).join(' and ');
  const swap = p.aliasTarget ? `uses the text of ${p.aliasTarget}` : 'no text swap';
  const warn = p.ignored.length > 0 ? ` — ignored by the package builder: "${p.ignored.join(' ')}"` : '';
  return `${when}: ${swap}${warn}`;
}
